/**
 * Truck fitment configurator (Shopify theme asset).
 *
 * Consolidated replacement for the previous version, which carried two parallel
 * renderers -- setFitmentResults() for landmark and setFitmentResultsTectonic()
 * for tectonic. The tectonic one ignored product identity entirely and painted
 * into fixed #tectonic-* ids, so a newly added edition of the Tectonic line
 * (e.g. "Max Edition", short_name "Max", live on 20 trucks as of this writing)
 * rendered as the generic Tectonic cover, and two editions on one truck would
 * collapse to whichever came last out of the loop.
 *
 * There is now ONE renderer. It keys every edition off the API's short_name and
 * looks for per-edition blocks (#<slug>-edition / #<slug>-edition-details); when
 * a page has no per-edition markup it falls back to a single generic block,
 * which is what keeps today's single-edition Tectonic PDP working unchanged
 * while letting new editions appear the moment their blocks are added.
 *
 * Endpoints (see src/Controller/ShopifyController.php in the admin repo):
 *   /shopify/makes | models | years | beds   -- cascade options
 *   /shopify/availability?truck_id&reporting_category_id  -- products for a truck
 *   /shopify/reviews?reporting_category_id&page
 */
(function () {
  'use strict';

  const API = 'https://admin.peragon.com/shopify/';

  /**
   * Reporting categories, keyed by the value of #truck-fitment-root's
   * data-reporting-category. `id` mirrors products.reporting_category_id in admin.
   *
   * priceDecimals records how each line has always rendered its price -- Landmark's
   * details block shows cents ("$1,099.00"), the Tectonic block shows whole dollars
   * ("$2,499"). It is declared here rather than sniffed from the markup, because both
   * sections now use the same [data-role="price"] slot and there is nothing in the
   * DOM left to tell them apart.
   */
  const CATEGORIES = {
    landmark: {
      id: 1,
      priceDecimals: true,
      //Edition to open on first, best-first. Anything the API returns that is not
      //listed still renders, it just is not auto-preferred -- and with no match at
      //all the highest-priced edition returned is opened rather than none.
      preference: ['limitedhdx', 'limitedhd', 'platinum', 'classic']
    },
    tectonic: {
      id: 4,
      priceDecimals: true, // #AchangeR: Show cents formatting ($2,499.00) matching Landmark
      preference: ['sport', 'pro', 'max', 'tectonic']
    }
  };

  /**
   * The vehicle cascade, in order. Each step owns its <select>, the endpoint that
   * fills it, and its placeholder. populateStep(), clearStepsFrom() and both
   * prefill paths are all driven off this one table, so the four near-identical
   * populateMake/Model/Year/Bed functions collapse into one -- and adding or
   * reordering a step is a one-line change instead of another copy of the same
   * fetch-and-fill block.
   *
   * A step sends every PRECEDING step's key as a query param, which is exactly
   * what the endpoints expect (models?make=, years?make=&model=, beds?make=&model=&year=).
   */
  const STEPS = [
    { key: 'make',  id: 'truck-make',  path: 'makes',  placeholder: 'Select The Make' },
    { key: 'model', id: 'truck-model', path: 'models', placeholder: 'Select The Model' },
    { key: 'year',  id: 'truck-year',  path: 'years',  placeholder: 'Select The Year', sort: 'numericDesc' },
    { key: 'bed',   id: 'truck-id',    path: 'beds',   placeholder: 'Select The Bed Length' }
  ];


  const ONE_HOUR_MS = 60 * 60 * 1000;

  //Line item properties that carry a ship promise. They are recomputed at render
  //and at click time, so any stale copy is stripped first.
  const SHIP_PROPS = [
    'Quickship',
    'Estimated Ship Date',
    '_ estimated ship date',
    '_estimated ship date'
  ];

  //IIFE-scope shared state
  let reportingCategory = 'landmark';
  let reportingCategoryId = CATEGORIES.landmark.id;
  let priceDecimals = CATEGORIES.landmark.priceDecimals;
  let editionPreference = CATEGORIES.landmark.preference;
  let root = null;
  let waitlistBtn = null;
  let selectedEdition = null;
  //The edition the shopper is looking at, kept when they change truck. "Change
  //vehicle" clears the open tab, not this, so a Shopping click on Platinum stays on
  //Platinum through a new year and bed instead of falling back to the preference.
  let wantedEdition = null;
  //{ id, label } of the truck the rendered covers were fitted to, straight off the
  ///availability response. Every cart line is stamped from this -- see truckProperties().
  let currentTruck = null;

  // ---------------------------------------------------------------------------
  // Small DOM / formatting helpers
  // ---------------------------------------------------------------------------

  function byId(id) {
    return document.getElementById(id);
  }

  //Every one of these was an unguarded getElementById(...).classList call before,
  //so a page missing (say) #no-cover-available threw a TypeError mid-cascade and
  //the selector silently stopped populating.
  function toggleHidden(el, hidden) {
    if (el) el.classList.toggle('hidden', hidden);
  }

  /**
   * The cover's products, lowest price first.
   *
   * cover.products is a JSON object keyed by product id. JS iterates integer-like
   * keys in ascending numeric order, which discards the endpoint's ORDER BY price
   * ASC -- so a newly created product (high id) sorted last regardless of price.
   * That is how a $2,199 Sport edition ended up as the one the single-block
   * Tectonic page rendered. Sorting here makes the order explicit; short_name
   * breaks price ties so the result is stable.
   */
  function coverProducts(cover) {
    return Object.values((cover && cover.products) || {}).sort((a, b) =>
      Number(a.price) - Number(b.price) ||
      String(a.short_name).localeCompare(String(b.short_name)));
  }

  function slugify(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // #AchangeR - Clean currency strings ($ and commas) before parsing to prevent NaN empty price output
  function formatPrice(value, excludeDecimals) {
    if (value === null || value === undefined || value === '') return '';
    const strVal = String(value).trim();
    const cleaned = strVal.replace(/[^0-9.]/g, '');
    let num = Number(cleaned);
    if (Number.isNaN(num) || cleaned === '') {
      console.warn('formatPrice: invalid number', value);
      return strVal.startsWith('$') ? strVal.slice(1) : strVal;
    }
    if (num > 100000 && !strVal.includes('.')) {
      num = num / 100;
    }
    return num.toLocaleString('en-US', {
      minimumFractionDigits: excludeDecimals ? 0 : 2,
      maximumFractionDigits: excludeDecimals ? 0 : 2
    });
  }

  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /**
   * Single JSON fetch path for every endpoint. Replaces eight copies of the same
   * fetch / res.ok / res.json() / try-catch block. Resolves to null on failure so
   * callers can bail without each owning a catch.
   */
  async function apiGet(path, params) {
    const url = new URL(API + path);
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, v);
    });
    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return await res.json();
    } catch (err) {
      console.error('[truck-fitment] ' + path + ' failed', err);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Cascade
  // ---------------------------------------------------------------------------

  function stepIndex(key) {
    return STEPS.findIndex(s => s.key === key);
  }

  function stepSelect(step) {
    return byId(step.id);
  }

  function stepValues() {
    const values = {};
    STEPS.forEach(step => {
      const select = stepSelect(step);
      values[step.key] = select ? select.value : '';
    });
    return values;
  }

  function resetSelect(select, placeholder, disablePlaceholder) {
    select.innerHTML = '';
    const option = document.createElement('option');
    option.value = '';
    option.textContent = placeholder;
    if (disablePlaceholder) {
      option.disabled = true;
      option.selected = true;
    }
    select.appendChild(option);
    select.disabled = true;
  }

  /**
   * Fill one cascade step from its endpoint, using the values of the steps before
   * it as query params. No-ops (leaving the placeholder in place) when an earlier
   * step is still empty.
   */
  async function populateStep(key) {
    const index = stepIndex(key);
    const step = STEPS[index];
    if (!step) return;

    const select = stepSelect(step);
    if (!select) {
      console.warn('No #' + step.id + ' select found on the page.');
      return;
    }

    resetSelect(select, step.placeholder, false);

    //Everything before this step has to be chosen before the endpoint can answer.
    const values = stepValues();
    const params = {};
    for (let i = 0; i < index; i++) {
      const prior = STEPS[i];
      if (!values[prior.key]) return;
      params[prior.key] = values[prior.key];
    }

    const options = await apiGet(step.path, params);
    if (!options) return;

    let entries = Object.entries(options);
    if (step.sort === 'numericDesc') {
      entries.sort(([a], [b]) => Number(b) - Number(a));
    }

    entries.forEach(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      select.appendChild(option);
    });

    select.disabled = false;
  }

  /**
   * Wipe every step from `key` onward back to its placeholder, and reset the
   * unavailable/submit rows.
   */
  function clearStepsFrom(key) {
    toggleHidden(byId('no-cover-available'), true);
    toggleHidden(byId('truck-submit-div'), false);

    const configButton = byId('truck-submit');
    if (configButton) configButton.disabled = true;

    const from = stepIndex(key);
    if (from < 0) return;

    for (let i = from; i < STEPS.length; i++) {
      const select = stepSelect(STEPS[i]);
      if (select) resetSelect(select, STEPS[i].placeholder, true);
    }
  }

  //Enable "Configure Now" only once every step has a value.
  function toggleConfigButton() {
    const configButton = byId('truck-submit');
    if (!configButton) return;
    const values = stepValues();
    configButton.disabled = !STEPS.every(step => values[step.key]);
  }

  // ---------------------------------------------------------------------------
  // Saved / linked truck
  // ---------------------------------------------------------------------------

  /**
   * Persist the current selection. `id` (bed) may be empty -- that is the URL-prefill
   * case, where make/model/year come from a marketing link and the shopper still has
   * to pick a bed length. Any stale bed id from a previous truck is cleared.
   */
  function saveTruckFitmentCookie() {
    const makeSelect  = byId('truck-make');
    const modelSelect = byId('truck-model');
    const yearSelect  = byId('truck-year');
    const bedSelect   = byId('truck-id');

    const truck = {
      make:        makeSelect ? makeSelect.value : '',
      model:       modelSelect ? modelSelect.value : '',
      year:        yearSelect ? yearSelect.value : '',
      id:          bedSelect ? bedSelect.value : '',
      make_label:  (makeSelect && makeSelect.selectedOptions[0] && makeSelect.selectedOptions[0].text) || '',
      model_label: (modelSelect && modelSelect.selectedOptions[0] && modelSelect.selectedOptions[0].text) || ''
    };

    if (!truck.make || !truck.model || !truck.year) {
      console.warn('Truck fitment incomplete, not saving cookie.');
      return;
    }

    //90 days
    const maxAge = 60 * 60 * 24 * 90;
    document.cookie = 'truck_fitment=' + encodeURIComponent(JSON.stringify(truck)) +
      '; path=/; max-age=' + maxAge + '; SameSite=Lax';
  }

  function getTruckFitmentFromCookie() {
    const match = document.cookie.split(';').map(c => c.trim())
      .find(c => c.indexOf('truck_fitment=') === 0);
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match.substring('truck_fitment='.length)));
    } catch (err) {
      console.error('Failed to parse truck_fitment cookie', err);
      return null;
    }
  }

  /**
   * make/model/year from the query string (?make=Ford&model=F-150&year=2025).
   * Bed length is never accepted from the URL -- the shopper picks one.
   */
  function getTruckFitmentFromURL() {
    try {
      const params = new URLSearchParams(window.location.search);
      const make  = (params.get('make')  || '').trim();
      const model = (params.get('model') || '').trim();
      const year  = (params.get('year')  || '').trim();
      return make ? { make, model, year } : null;
    } catch (err) {
      return null;
    }
  }

  /**
   * make/model(/year) of the page itself -- a truck cover page (metaobject) renders
   * them onto #truck-fitment-root as data-page-*. Model pages have no year; make
   * pages have no model either.
   */
  function getTruckFitmentFromPage() {
    if (!root) return null;
    const make  = (root.dataset.pageMake  || '').trim();
    const model = (root.dataset.pageModel || '').trim();
    const year  = (root.dataset.pageYear  || '').trim();
    return make ? { make, model, year } : null;
  }

  /**
   * Does the saved truck belong on this page? Same make and model, and the same year
   * when the page is a year page. A model page accepts any saved year of that model,
   * a make page any saved truck of that make.
   */
  function savedTruckMatchesPage(saved, page) {
    if (!saved || !page) return false;
    const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    return same(saved.make, page.make) && (!page.model || same(saved.model, page.model)) &&
      (!page.year || same(saved.year, page.year));
  }

  /**
   * Handle of a truck cover page, built the same way the import did:
   * "Ford", "F-150", "2026" -> "ford-f-150-2026".
   */
  function truckPageHandle(...parts) {
    return parts.filter(Boolean).join(' ').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /**
   * On a truck cover page everything below the selector (H1, copy, fitment, links)
   * is about the page's truck. When the shopper picks a different truck, take them
   * to that truck's page so the page and the selector never disagree.
   *
   * Fires once the selection reaches the page's own level -- the make on a make
   * page, the model on a model page, the year on a year page -- so switching truck
   * costs one page load, not one per step. Tectonic only has pages where it fits,
   * so each candidate is checked before leaving: year page, then model page, then
   * the product page, which prefills any truck from ?make=&model=&year=.
   */
  let leavingForTruckPage = false;
  async function followTruckToItsPage(stepKey) {
    const page = getTruckFitmentFromPage();
    const base = root && root.dataset.pageBase;
    if (!page || !base || leavingForTruckPage) return;

    const pageLevel = page.year ? 'year' : (page.model ? 'model' : 'make');
    if (stepKey !== pageLevel) return;

    //Steps below the page's level are ignored: this runs before the change handler
    //clears them, so they can still hold the previous truck's year.
    const levels = ['make', 'model', 'year'];
    const values = stepValues();
    const picked = {};
    levels.forEach((key, i) => { picked[key] = i <= levels.indexOf(pageLevel) ? values[key] : ''; });
    if (!picked[pageLevel]) return;
    const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    if (same(picked.make, page.make) && same(picked.model, page.model) && same(picked.year, page.year)) return;

    leavingForTruckPage = true;
    //Carries make/model/year to the next page (saves nothing until a year is picked).
    saveTruckFitmentCookie();

    const candidates = [];
    if (picked.year) candidates.push(base + truckPageHandle(picked.make, picked.model, picked.year));
    if (picked.model) candidates.push(base + truckPageHandle(picked.make, picked.model));
    if (pageLevel === 'make') candidates.push(base + truckPageHandle(picked.make));

    for (const url of candidates) {
      try {
        const res = await fetch(url, { method: 'HEAD', credentials: 'same-origin' });
        if (res.ok) {
          window.location.assign(url);
          return;
        }
      } catch (err) {
        //Network hiccup: try the next candidate.
      }
    }

    const productUrl = root.dataset.productUrl;
    if (productUrl) {
      const params = new URLSearchParams();
      ['make', 'model', 'year'].forEach(key => { if (picked[key]) params.set(key, picked[key]); });
      window.location.assign(productUrl + '?' + params.toString());
      return;
    }
    leavingForTruckPage = false;
  }

  /**
   * Tell the truck page content block (assets/truck-page-content.js) which truck the
   * selector holds, so a model page can show the picked year's fitment. Fired on
   * every selection change, prefill and "change vehicle"; the latest state is also
   * kept on window.PeragonTruckFitment for a listener that loads after the event.
   *
   *   truck-fitment:truck  { make, model, year, bed }  (bed = truck id, '' if none)
   *   truck-fitment:cover  { truckId, fits }           after each availability check
   */
  const fitmentState = window.PeragonTruckFitment = window.PeragonTruckFitment || {};
  function announceTruck(truck) {
    const values = truck || stepValues();
    fitmentState.truck = {
      make: values.make || '', model: values.model || '', year: values.year || '',
      bed: values.bed || values.id || ''
    };
    document.dispatchEvent(new CustomEvent('truck-fitment:truck', { detail: fitmentState.truck }));
  }
  function announceCover(truckId, cover) {
    fitmentState.cover = { truckId: String(truckId || ''), fits: !!(cover && !cover.has_error && cover.has_products) };
    document.dispatchEvent(new CustomEvent('truck-fitment:cover', { detail: fitmentState.cover }));
  }

  /**
   * The Shopify variant id in the URL, if any (?variant=123).
   */
  function variantFromURL() {
    try {
      return (new URLSearchParams(window.location.search).get('variant') || '').trim();
    } catch (err) {
      return '';
    }
  }

  /**
   * Mirror the open edition's Shopify variant into the URL as ?variant=<id>, so a
   * reload -- or a shared/bookmarked link -- reopens the edition the shopper was
   * actually looking at instead of the design's default.
   *
   * replaceState, not pushState: switching edition tabs is not a navigation, and one
   * history entry per click would trap the shopper behind a dozen Backs. Rebuilding
   * from the current href preserves every other param -- theme previews carry
   * preview_theme_id/template and marketing deep links carry make/model/year.
   */
  function syncVariantParam(variantId) {
    if (!window.history || !window.history.replaceState) return;
    try {
      const url = new URL(window.location.href);
      if (variantId) {
        if (url.searchParams.get('variant') === String(variantId)) return;
        url.searchParams.set('variant', variantId);
      } else {
        if (!url.searchParams.has('variant')) return;
        url.searchParams.delete('variant');
      }
      //Silent: this mirrors the open edition, it is not a navigation. See
      //snippets/native-history.liquid for why the wrapped method must not be used.
      theme.replaceStateSilent(window.history.state, '', url.toString());
    } catch (err) {
      //A malformed href must never take the configurator down with it.
    }
  }

  /**
   * Which rendered edition owns this variant id. Null when the id belongs to another
   * truck -- a stale ?variant= from a previous vehicle must fall back to the default
   * rather than opening nothing.
   *
   * Null too when SEVERAL editions share the id, which is the state of the Tectonic
   * product today: all 21 Shopify variants are option1 "Max" (one per truck SKU), so
   * Sport and Pro have no variant of their own and every tab falls back to the same
   * id. Guessing between them would reopen an arbitrary tab and override the design's
   * preference, so this stays inert until Shopify has per-edition variants -- at which
   * point it starts working with no further change here.
   */
  function editionForVariant(rendered, variantId) {
    if (!variantId) return null;
    const matches = rendered.filter(slug => {
      const details = byId(slug + '-edition-details');
      const product = details && details._product;
      return product && String(product.variant_id) === String(variantId);
    });
    return matches.length === 1 ? matches[0] : null;
  }

  /**
   * Shopify's variants of this page's product, rendered by snippets/truck-fitment.liquid.
   */
  function shopifyVariants() {
    const el = byId('truck-fitment-shopify-variants');
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent || '[]');
      return Array.isArray(parsed) ? parsed : Object.values(parsed);
    } catch (err) {
      return [];
    }
  }

  /**
   * The rendered edition a variant id is an edition OF, even when the variant is
   * another truck's part. A Shopping link names one bed's Platinum; once the shopper
   * picks a different bed, editionForVariant() finds no match, but the edition they
   * clicked is still Platinum. Read from the variant's Edition option
   * ("Platinum Edition", "LimitedHDx Edition", "Max").
   */
  function editionNamedByVariant(rendered, variantId) {
    if (!variantId) return null;
    const variant = shopifyVariants().find(v => String(v.id) === String(variantId));
    if (!variant) return null;
    const slug = slugify(String(variant.option1 || '').replace(/\bedition\b/i, ''));
    return rendered.includes(slug) ? slug : null;
  }

  //Marketing links may not match the API's casing, so match case-insensitively and
  //then assign the option's own value to get the canonical spelling back.
  function selectValueCI(select, rawValue) {
    const target = String(rawValue || '').trim().toLowerCase();
    if (!select || !target) return false;
    const option = Array.from(select.options)
      .find(o => o.value && o.value.trim().toLowerCase() === target);
    if (!option) return false;
    select.value = option.value;
    return true;
  }

  /**
   * Walk the cascade selecting saved values as we go. Shared by the cookie and URL
   * paths, which previously duplicated the whole make -> model -> year -> bed ladder.
   *
   * `stopBefore` lets the URL path populate bed options but leave them unselected.
   */
  async function prefillTruck(truck, stopBefore) {
    for (const step of STEPS) {
      await populateStep(step.key);
      if (step.key === stopBefore) return true;

      const select = stepSelect(step);
      const value = step.key === 'bed' ? truck.id : truck[step.key];
      if (!selectValueCI(select, value)) {
        console.warn('Saved/linked ' + step.key + ' not found in options:', value);
        return false;
      }
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Ship date / countdown
  // ---------------------------------------------------------------------------

  function getFormattedDateOffset(dayOffset) {
    const d = new Date();
    if (dayOffset) d.setDate(d.getDate() + dayOffset);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[d.getMonth()] + ' ' + d.getDate();
  }

  /**
   * Read the *rendered* shipping element (not the raw API HTML string) and return a
   * clean, human-readable ship date for the cart line item.
   *
   * The API's "Estimated Ship Date" HTML can contain a same-day quickship countdown
   * (<span id="countdown">) whose visible text is only filled in client-side by
   * startShipCountdown(). Regexing the raw string would capture the empty span and
   * would also fail to strip the "Quickship:" label, whose icon markup breaks a
   * simple prefix match. Reading the live node guarantees we get what is on screen.
   */
  function extractCleanShipDate(shipEl) {
    if (!shipEl) return '';

    const temp = shipEl.cloneNode(true);

    //Remove the quickship *label* (icon + "Quickship:") but not the countdown value --
    //both carry the .quickship class, so we cannot blindly strip every match.
    const countdown = temp.querySelector('#countdown, [data-role="countdown"]');
    temp.querySelectorAll('.quickship').forEach(el => {
      if (el !== countdown) el.remove();
    });

    let text = temp.textContent
      .replace(/^(quickship|quick ship)\s*:?\s*/i, '')
      .replace(/^ships\s*:\s*between\s*/i, 'Between ')
      .replace(/^ships\s*:\s*/i, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (/if\s+ordered\s+within/i.test(text)) {
      text = text.replace(/\s+if\s+ordered\s+within.*$/i, '').trim();
    }

    if (/^(ships\s+)?today$/i.test(text)) {
      text = 'Ships TODAY - ' + getFormattedDateOffset(0);
    } else if (/^(ships\s+)?tomorrow$/i.test(text)) {
      text = 'Ships TOMORROW - ' + getFormattedDateOffset(1);
    }

    return text;
  }

  /**
   * Start (or restart) the 1-second same-day countdown inside a shipping container.
   * No-op for containers without one.
   *
   * The interval handle lives on the element (el._countdownTimer) rather than in one
   * shared variable so several editions can count down at once (Limited HD and
   * Limited HDx both showing a same-day window) without clobbering each other.
   */
  function startShipCountdown(containerEl, onExpire) {
    const el = containerEl ? containerEl.querySelector('#countdown, [data-role="countdown"]') : null;
    if (!el) return;

    if (el._countdownTimer) {
      clearInterval(el._countdownTimer);
      el._countdownTimer = null;
    }

    const total = parseInt(el.getAttribute('data-seconds'), 10) || 0;
    let spent = parseInt(el.getAttribute('data-spent'), 10) || 0;
    if (total - spent <= 0) return;

    const render = () => {
      const remaining = total - spent;
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = Math.floor(remaining % 60);
      el.innerHTML = h + 'h ' + m + 'm ' + s + 's ';
    };

    //Render immediately instead of waiting a full second for the first tick.
    render();

    el._countdownTimer = setInterval(function () {
      spent += 1;
      el.setAttribute('data-spent', spent);
      if (total - spent <= 0) {
        clearInterval(el._countdownTimer);
        el._countdownTimer = null;
        if (typeof onExpire === 'function') onExpire();
        return;
      }
      render();
    }, 1000);
  }

  // ---------------------------------------------------------------------------
  // Cart line item properties
  // ---------------------------------------------------------------------------

  function cleanFitmentNotes(raw) {
    if (!raw) return '';

    //Drop links and turn <br> into newlines before flattening the rest.
    let cleaned = String(raw)
      .replace(/<a[^>]*href=['"][^'"]+['"][^>]*>.*?<\/a>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n');

    //Decode entities (&trade; -> (TM), &amp; -> &) and strip remaining tags.
    if (typeof DOMParser !== 'undefined') {
      cleaned = new DOMParser().parseFromString(cleaned, 'text/html').body.textContent || '';
    } else {
      cleaned = stripHtml(cleaned);
    }

    //Tidy the punctuation artifacts left behind by the removed link ("liners . .").
    return cleaned
      .replace(/\s+/g, ' ')
      .replace(/\s+\./g, '.')
      .replace(/\.{2,}/g, '.')
      .replace(/\.\s*\.+/g, '.')
      .trim();
  }

  function fitmentNotesLink(raw) {
    const match = String(raw || '').match(/<a[^>]*href=['"]([^'"]+)['"][^>]*>.*?<\/a>/i);
    return match ? match[1] : null;
  }

  /**
   * Normalise one properties object in place: drop any stale ship promise, apply the
   * current one, and split "Fitment Notes" into clean text plus a hidden link.
   * Shared by the permalink, the stored cart_add payload and the click handler,
   * which each had their own copy of this block.
   */
  function normaliseProperties(properties, shipDate) {
    if (!properties) return properties;

    SHIP_PROPS.forEach(key => { delete properties[key]; });
    if (shipDate) properties['Estimated Ship Date'] = shipDate;

    const notes = properties['Fitment Notes'];
    if (notes) {
      const link = fitmentNotesLink(notes);
      if (link) properties['_Fitment Notes Link'] = link;
      properties['Fitment Notes'] = cleanFitmentNotes(notes);
    }

    return properties;
  }

  function normaliseCartAdd(cartAdd, shipDate) {
    if (!cartAdd || !Array.isArray(cartAdd.items)) return cartAdd || null;
    cartAdd.items.forEach(item => normaliseProperties(item.properties, shipDate));
    return cartAdd;
  }

  function withUrl(url, fn) {
    if (!url) return url;
    try {
      const isRelative = !/^(https?:)?\/\//.test(url);
      const urlObj = new URL(url, isRelative ? window.location.origin : undefined);
      fn(urlObj);
      return isRelative ? urlObj.pathname + urlObj.search : urlObj.toString();
    } catch (err) {
      return url;
    }
  }

  //Same normalisation as normaliseProperties(), applied to a /cart/add permalink.
  function normalisePermalink(url, shipDate) {
    return withUrl(url, urlObj => {
      SHIP_PROPS.forEach(key => urlObj.searchParams.delete('properties[' + key + ']'));
      if (shipDate) urlObj.searchParams.set('properties[Estimated Ship Date]', shipDate);

      const notes = urlObj.searchParams.get('properties[Fitment Notes]');
      if (notes) {
        const link = fitmentNotesLink(notes);
        if (link) urlObj.searchParams.set('properties[_Fitment Notes Link]', link);
        urlObj.searchParams.set('properties[Fitment Notes]', cleanFitmentNotes(notes));
      }
    });
  }

  /**
   * The truck a cover line MUST carry into the order. Same keys the admin puts on its
   * own cart_add / add_to_cart payloads, so admin's order import reads them either way.
   *
   * Taken from the /availability response rather than the cascade selects: the
   * selects can be empty (a saved truck renders before they refill) and the derived
   * Tectonic editions build their own payload, which is how order #3372 reached
   * checkout with no truck at all.
   */
  function truckProperties() {
    if (!currentTruck || !currentTruck.id) return {};
    const properties = { _truck_id: String(currentTruck.id) };
    if (currentTruck.label) properties['Configured Truck'] = currentTruck.label;
    return properties;
  }

  //_truck_id is what admin fulfils against; "Configured Truck" is only its label.
  function hasTruck(properties) {
    return !!(properties && properties._truck_id);
  }

  function isCartAddUrl(url) {
    try {
      return new URL(url, window.location.origin).pathname.indexOf('/cart/add') === 0;
    } catch (err) {
      return false;
    }
  }

  //Writes truckProperties() onto a /cart/add permalink, so a middle-click, a
  //"open in new tab" or the failed-AJAX fallback still carries the truck. Anything
  //else (the "#add-to-cart" placeholder) is returned untouched.
  function stampTruckPermalink(url) {
    if (!url || !isCartAddUrl(url)) return url;
    const truck = truckProperties();
    return withUrl(url, urlObj => {
      Object.keys(truck).forEach(key => urlObj.searchParams.set('properties[' + key + ']', truck[key]));
    });
  }

  function stampTruckCartAdd(cartAdd) {
    if (!cartAdd || !Array.isArray(cartAdd.items)) return cartAdd;
    const truck = truckProperties();
    cartAdd.items.forEach(item => { item.properties = Object.assign({}, item.properties, truck); });
    return cartAdd;
  }

  function permalinkHasTruck(url) {
    if (!url || !isCartAddUrl(url)) return false;
    try {
      return !!new URL(url, window.location.origin).searchParams.get('properties[_truck_id]');
    } catch (err) {
      return false;
    }
  }

  //Blocks an add that would reach the cart without a truck and tells the shopper why.
  //A cover with no truck on the line cannot be fulfilled without calling them.
  function blockAddWithoutTruck(link) {
    console.error('[truck-fitment] blocked add to cart with no truck on the line', link.href);
    //Outside the .submit wrapper, which is itself styled as the button.
    const anchor = link.closest('.submit') || link;
    const parent = anchor.parentNode;
    let message = parent && parent.querySelector('.truck-fitment-cta-error');
    if (!message && parent) {
      message = document.createElement('p');
      message.className = 'truck-fitment-cta-error';
      message.setAttribute('role', 'alert');
      parent.insertBefore(message, anchor.nextSibling);
    }
    if (message) {
      message.textContent = "We couldn't confirm your truck. Please use Change Vehicle to select it again before adding this cover.";
    }
  }

  //Selected cascade labels are carried into the order as hidden properties -- but
  //only when the selects describe the same truck the covers were fitted to.
  function selectionProperties() {
    const properties = {};
    const bedSelect = byId('truck-id');
    if (!currentTruck || !bedSelect || bedSelect.value !== String(currentTruck.id)) return properties;
    document.querySelectorAll('.truck-fitment-block__field select').forEach(select => {
      if (!select.value) return;
      const key = select.id === 'truck-id' ? 'truck-bed' : select.id;
      properties['_' + key] = select.options[select.selectedIndex].text;
    });
    return properties;
  }

  // ---------------------------------------------------------------------------
  // Edition rendering -- the single renderer that replaces the landmark/tectonic split
  // ---------------------------------------------------------------------------

  /**
   * Resolve the DOM for one edition.
   *
   * Per-edition pages (landmark) have #<slug>-edition tabs and #<slug>-edition-details
   * blocks. Single-edition pages (today's Tectonic PDP) have neither -- one fixed
   * block addressed by flat ids (#tectonic-price, #tectonic-shipping,
   * #tectonic-description, #tectonic-add-to-cart). Falling back to that block is
   * what lets a brand new edition render on a page whose markup only knows about
   * one, instead of the shopper seeing nothing.
   */
  function resolveEdition(slug) {
    if (slug === 'tectonic') slug = 'pro'; // #AchangeR: Map base tectonic API product to pro edition
    const details = byId(slug + '-edition-details');
    return {
      slug: slug,
      tab: byId(slug + '-edition'),
      //With no per-edition block, scope slot lookups to the whole results container
      //so the single-edition markup's [data-role] slots (e.g. the Affirm widget,
      //which sits outside any details block on the Tectonic section) still resolve.
      details: details || byId('fitment_results'),
      perEdition: !!details,
      callout: byId(slug + '-quickship-callout')
    };
  }

  /**
   * Find a slot for one edition.
   *
   * Scoped [data-role] inside the edition's own block wins. The flat
   * #<category>-<suffix> id is consulted ONLY for an edition with no block of its
   * own -- otherwise several editions rendering in turn would all write the same
   * page-level element and the last one would win, which is exactly the bug that
   * made the Tectonic page show whichever edition sorted last.
   */
  function slot(edition, role, legacySuffix) {
    if (edition.details) {
      const scoped = edition.details.querySelector('[data-role="' + role + '"]');
      if (scoped) return scoped;
    }
    if (edition.perEdition || !legacySuffix) return null;
    return byId(reportingCategory + '-' + legacySuffix);
  }

  /**
   * Render one product into its edition block.
   *
   * @param {Object} edition Result of resolveEdition().
   * @param {Object} product One entry from cover.products.
   * @param {String} truckId Truck being configured, for the countdown-expiry refresh.
   */
  function renderEdition(edition, product, truckId) {
    const details = edition.details;

    //Remember which product this block is showing, so editionPicked() can update the
    //page-level slots (heading suffix, header price) when the shopper switches tabs.
    if (details) details._product = product;

    //Tab chip price has always been whole dollars.
    if (edition.tab) {
      const tabPrice = edition.tab.querySelector('.price');
      if (tabPrice) tabPrice.textContent = '$' + formatPrice(product.price, true);
    }

    //Landmark renders cents, Tectonic whole dollars -- see CATEGORIES.priceDecimals.
    const priceSlot = slot(edition, 'price', 'price');
    if (priceSlot) priceSlot.textContent = '$' + formatPrice(product.price, !priceDecimals);

    const shipSlot = slot(edition, 'shipping', 'shipping');
    if (shipSlot) {
      shipSlot.innerHTML = product['Estimated Ship Date'] || '';
      //One countdown wiring for every line. On expiry we re-check availability and
      //re-render, keeping whichever edition the shopper had open.
      startShipCountdown(shipSlot, () => refreshCover(truckId, { keepSelection: true }));
    }

    const cta = slot(edition, 'cta', 'add-to-cart');
    if (cta) {
      //Read the ship date back off the rendered slot (post countdown render) rather
      //than out of the raw API string, so a same-day window carries its actual time.
      const shipDate = extractCleanShipDate(shipSlot);
      cta.href = stampTruckPermalink(normalisePermalink(product.add_to_cart, shipDate));
      cta._cartAdd = stampTruckCartAdd(normaliseCartAdd(product.cart_add, shipDate));
    }

    const affirmEl = slot(edition, 'affirm', 'affirm');
    if (affirmEl) affirmEl.dataset.amount = product.price * 100;

    //Description is deliberately NOT written from the API. Every edition block ships
    //its own authored copy in Liquid -- Sport/Pro/Max carry their own warranty wording,
    //Landmark's four editions carry formatted marketing copy -- and writing the admin's
    //description over it replaced that with one flattened blurb the moment a truck was
    //configured. Shopify owns this content; admin owns fitment.

    //Opt-in: the API's product title, so a section whose heading is hardcoded to one
    //edition can show the edition actually fitted (e.g. "... - Max Edition"). Inert
    //until the markup adds the slot, so no page changes by having this here.
    const titleEl = slot(edition, 'edition-title', null);
    if (titleEl) titleEl.textContent = product.title || '';

    //The API sends _quickship as 0/1, so the old `!== true` check hid this callout
    //unconditionally.
    toggleHidden(edition.callout, !Number(product._quickship));
  }

  /**
   * Render an /shopify/availability response. One path for every product line.
   */
  function renderCover(cover, options) {
    options = options || {};

    //Release the cookie-prefill suppression class so the configurator can show.
    document.documentElement.classList.remove('truck-fitment-prefill');

    const resultsDiv = byId('fitment_results');
    const configDiv = byId('configure_fitment');

    if (!cover || cover.has_error || !cover.has_products) {
      currentTruck = null;
      toggleHidden(resultsDiv, true);
      toggleHidden(configDiv, false);
      toggleHidden(byId('truck-submit-div'), true);
      toggleHidden(byId('no-cover-available'), false);
      toggleHidden(waitlistBtn, !(cover && cover.has_waitlist));
      rememberWaitlistTruck(true);
      return;
    }

    rememberWaitlistTruck(false);
    document.querySelectorAll('.truck-fitment-cta-error').forEach(el => el.remove());

    const truckId = cover.truck_id || options.truckId || null;
    //Set before any edition renders: renderEdition() stamps it onto every CTA.
    currentTruck = truckId ? { id: String(truckId), label: cover.configured_truck || cover.configured_truck_short || '' } : null;

    // #AchangeR: Re-title for the selected truck (Landmark and Tectonic)
    const resultsH1 = byId('fitment-results-h1');
    const defaultTemplate = reportingCategory === 'tectonic'
      ? 'Tectonic™ {truck} Tonneau Cover'
      : 'Retractable {truck} Tonneau Cover';
    const h1Template = (root && root.dataset.h1Template) || defaultTemplate;
    if (resultsH1 && h1Template && cover.configured_truck_short) {
      const truckText = String(cover.configured_truck_short).replace(/-/g, '‑');
      const newTitleText = h1Template.replace('{truck}', truckText);
      const newIconImg = resultsH1.querySelector('.new-icon');
      if (newIconImg) {
        resultsH1.innerHTML = newIconImg.outerHTML + ' ' + newTitleText + '<span data-role="edition-suffix"></span>';
      } else {
        resultsH1.textContent = newTitleText;
      }
    }

    setVehicleFromCover(cover.configured_truck_short, cover.configured_truck);

    //Reset every edition tab / block; only what the API returns is shown again.
    document.querySelectorAll('#edition-selector .col.edition').forEach(el => {
      el.classList.add('hidden');
      el.classList.remove('selected');
    });
    document.querySelectorAll('#edition-details .details').forEach(el => el.classList.add('hidden'));

    const rendered = [];
    let fitmentNote = cover.configured_truck_notes || null;
    let fallbackUsed = 0;

    const shopifyVariantsList = shopifyVariants();

    const isTectonicGBB = reportingCategory === 'tectonic' || !!byId('sport-edition');

    //cover.products is keyed by product id, and JS iterates integer-like keys in
    //ascending NUMERIC order -- which silently threw away the endpoint's
    //ORDER BY price ASC. Sort explicitly so "highest priced" is meaningful and the
    //result does not depend on which product row happens to be newest.
    coverProducts(cover).forEach(product => {
      let slug = slugify(product.short_name);
      if (slug === 'tectonic' && isTectonicGBB) {
        return;
      }
      const edition = resolveEdition(slug);

      //Nothing to render into at all -- say so instead of failing silently, which is
      //how the "Max" edition went missing.
      if (!edition.details) {
        console.warn('[truck-fitment] no markup for edition "' + slug + '" (' +
          product.short_name + ') -- add #' + slug + '-edition-details to the section.');
        return;
      }

      //Two editions sharing the single-edition fallback block means the second
      //silently overwrites the first. Nothing here can fix that -- the section needs
      //per-edition blocks -- but it should be loud rather than invisible.
      if (!edition.perEdition && ++fallbackUsed > 1) {
        console.warn('[truck-fitment] ' + fallbackUsed + ' editions returned but the ' +
          'section has only one generic block; "' + product.short_name + '" is ' +
          'overwriting the previous one. Add per-edition #<slug>-edition-details blocks.');
      }

      renderEdition(edition, product, truckId);
      if (edition.tab) edition.tab.classList.remove('hidden');
      //Track by block, not by tab: a line with per-edition blocks but no tabs (a
      //single-edition page that still wants edition-specific markup) must still get
      //its block un-hidden by editionPicked().
      if (edition.perEdition) rendered.push(slug);

      if (!fitmentNote) {
        fitmentNote = product['Fitment Notes'] || product.configured_truck_notes || null;
      }
    });

    // #AchangeR: Ensure all 3 GBB edition tabs (sport, pro, max) are populated and unhidden for Tectonic
    if (isTectonicGBB) {
      const productsList = coverProducts(cover);
      const baseProduct = productsList.length ? productsList[0] : null;

      //The admin returns one row per truck x edition, each carrying its OWN ship promise
      //(Estimated Ship Date / _quoted / _quickship / starts_shipping_on). Deriving every
      //tab from baseProduct made all three show the cheapest row's promise and silently
      //swallowed a per-edition release date -- and it put the wrong _quoted and
      //_product_id on the cart line. Render an edition from its own row (see `fitted`).

      const tectonicEditionsConfig = {
        sport: { title: 'Tectonic™ Sport Dual-Motion Tri-Fold Truck Bed Cover', short_name: 'Sport', price: 2299 },
        pro: { title: 'Tectonic™ Pro Dual-Motion Tri-Fold Truck Bed Cover', short_name: 'Pro', price: 2499 },
        max: { title: 'Tectonic™ Max Dual-Motion Tri-Fold Truck Bed Cover', short_name: 'Max', price: 2899 }
      };

      //Pick each edition's variant by IDENTITY, never by position. This used to find
      //the cheapest row's index in its edition's variant list and reuse that index in
      //the other two lists, which only works while all three lists are in the same
      //truck order. Shopify's Pro variants were not (PRO 10-21 then PRO 1-9), so every
      //Pro order got the cover for the truck nine places on -- a Ram 2500 was sold the
      //2016-2023 Tacoma 6'0 part. Now a variant is chosen only if it IS one of the
      //admin rows returned for this truck: same variant id, or same base part number.
      //Pro and Sport part numbers are disjoint, so each edition matches at most one row.
      const baseSku = value => String(value || '').trim().toLowerCase().replace(/-[0-9]+$/, '');
      const rowVariantId = p => String((p.cart_add && p.cart_add.items && p.cart_add.items[0] && p.cart_add.items[0].id) || p.variant_id || '');
      const rowByVariantId = {};
      const rowBySku = {};
      productsList.forEach(p => {
        const id = rowVariantId(p);
        if (id) rowByVariantId[id] = p;
        const sku = baseSku(p.sku);
        if (sku) rowBySku[sku] = p;
      });

      //{ sport: { variant, row }, ... } -- an edition is absent when none of its
      //variants fits this truck, rather than falling back to an arbitrary cover.
      const fitted = {};
      ['sport', 'pro', 'max'].forEach(s => {
        shopifyVariantsList
          .filter(v => String(v.option1 || v.title || '').toLowerCase().includes(s))
          .some(v => {
            const row = rowByVariantId[String(v.id)] ||
              rowBySku[baseSku(v.sku)] ||
              rowBySku[baseSku(v.option2)];
            if (row) fitted[s] = { variant: v, row: row };
            return !!row;
          });
      });

      ['sport', 'pro', 'max'].forEach(s => {
        const tab = byId(s + '-edition');
        const match = fitted[s];
        if (!match) {
          console.warn('[truck-fitment] no ' + s + ' variant fits truck ' + truckId + '; hiding that edition.');
          return;
        }
        if (tab) tab.classList.remove('hidden');

        const detailsEl = byId(s + '-edition-details');
        if (detailsEl && baseProduct) {
          const config = tectonicEditionsConfig[s] || { title: baseProduct.title, short_name: s, price: baseProduct.price };
          const variantId = String(match.variant.id);

          //The admin row that fits this truck for this edition, so the cart line carries
          //that row's own _product_id / ship promise instead of the cheapest row's.
          //Matched by part number because the admin's short_title is "Tectonic" for both
          //Pro and Sport, so the row cannot be found by edition name.
          const source = match.row;

          const baseProps = (source.cart_add && source.cart_add.items && source.cart_add.items[0] && source.cart_add.items[0].properties) || {};
          const derivedProps = Object.assign({}, baseProps, {
            'Edition': 'Tectonic ' + config.short_name
          });

          const derivedProduct = Object.assign({}, source, {
            title: config.title,
            short_name: config.short_name,
            price: config.price,
            compare_at: config.price,
            variant_id: variantId,
            add_to_cart: `/cart/add?id=${variantId}`,
            cart_add: {
              items: [
                {
                  id: variantId,
                  quantity: 1,
                  properties: derivedProps,
                  selling_plan: null
                }
              ]
            }
          });

          const edition = resolveEdition(s);
          renderEdition(edition, derivedProduct, truckId);
          if (!rendered.includes(s)) rendered.push(s);

          if (!fitmentNote) {
            fitmentNote = baseProduct['Fitment Notes'] || baseProduct.configured_truck_notes || null;
          }
        }
      });
    }

    document.querySelectorAll('#fitment-note, #cover-fitment-note').forEach(el => {
      el.innerHTML = fitmentNote || '';
      el.style.paddingTop = fitmentNote ? '6px' : '0px';
    });

    //Pick an edition: keep the shopper's choice across a countdown refresh, else the
    //design's preference, else the last (highest-priced) edition returned. The old
    //hardcoded .find() returned undefined for any unknown line and selected nothing.
    if (rendered.length) {
      const keep = options.keepSelection && rendered.includes(selectedEdition) ? selectedEdition : null;
      //?variant= outranks the design's preference so a reload restores the shopper's
      //edition, but never outranks a selection being deliberately kept across a
      //countdown refresh. After that, the edition the shopper had open before
      //changing truck, then the edition their link named: a new year or bed must
      //not move them to a dearer edition.
      editionPicked(keep ||
        editionForVariant(rendered, variantFromURL()) ||
        (rendered.includes(wantedEdition) ? wantedEdition : null) ||
        editionNamedByVariant(rendered, variantFromURL()) ||
        editionPreference.find(e => rendered.includes(e)) ||
        rendered[rendered.length - 1]);
    }

    //Refresh Affirm once, after every block is populated.
    if (typeof affirm !== 'undefined' && affirm.ui && typeof affirm.ui.refresh === 'function') {
      affirm.ui.refresh();
    }

    toggleHidden(resultsDiv, false);
    toggleHidden(configDiv, true);
  }

  function setVehicleFromCover(title, bed) {
    document.querySelectorAll('#selected-truck, #cover-selected-truck').forEach(container => {
      const shortTitle = container.querySelector('.short-title');
      if (shortTitle) shortTitle.textContent = title || '';

      const bedLength = container.querySelector('.bed-length');
      if (!bedLength) return;
      //"2024 Ford F-150 with 66'' Bed" -> "66'' Bed"
      const index = String(bed || '').toLowerCase().indexOf(' with ');
      bedLength.textContent = index === -1 ? (bed || '') : bed.slice(index + ' with '.length);
    });
  }

  function editionPicked(slug) {
    if (!slug) return;
    selectedEdition = slug;
    wantedEdition = slug;

    document.querySelectorAll('#edition-selector .col.edition').forEach(el => el.classList.remove('selected'));
    const tab = byId(slug + '-edition');
    if (tab) tab.classList.add('selected');

    const details = byId(slug + '-edition-details');
    document.querySelectorAll('#edition-details .details').forEach(el => el.classList.add('hidden'));
    toggleHidden(details, false);

    const product = details && details._product;
    applySelectedEdition(product);
    syncVariantParam(product && product.variant_id);
    updateEditionMedia(slug);
  }

  /**
   * Fill the page-level slots that describe whichever edition is currently open.
   * These live OUTSIDE the edition blocks (in the heading and the header price), so
   * they cannot be written from renderEdition -- every edition would overwrite them
   * in turn and the last one would win.
   *
   *   [data-role="selected-price"]  the open edition's price
   *   [data-role="edition-suffix"]  just the tier, e.g. " - Sport Edition", so a
   *                                 heading keeps its own marketing copy
   *   [data-role="edition-title"]   the API's full product title
   */
  function applySelectedEdition(product) {
    if (!product) return;

    document.querySelectorAll('[data-role="selected-price"]').forEach(el => {
      el.textContent = '$' + formatPrice(product.price, !priceDecimals);
    });

    //Truck pages render the header price server-side -- "From ..." or, for a Google
    //Shopping ?variant= link, that variant's price. Once an edition is open it names
    //the price, so a different pick never sits under a stale figure. Cents to match
    //Shopify's money format.
    document.querySelectorAll('[data-truck-page-price] .price__regular').forEach(el => {
      el.textContent = '$' + formatPrice(product.price, false);
    });

    //"Tectonic(TM) ... Cover - Sport Edition" -> " - Sport Edition"; base products
    //with no " - " suffix clear the slot instead.
    const title = String(product.title || '');
    const cut = title.lastIndexOf(' - ');
    const suffix = cut === -1 ? '' : title.slice(cut);
    document.querySelectorAll('[data-role="edition-suffix"]').forEach(el => {
      el.textContent = suffix;
    });

    document.querySelectorAll('[data-role="edition-title"]').forEach(el => {
      el.textContent = title;
    });

    //[data-role="selected-description"] is intentionally left alone for the same reason
    //as the per-edition copy above: the section's own text stays put when tabs change.
  }

  // #AchangeR: Landmark-compatible gallery media filter for #sport-edition, #pro-edition, #max-edition
  function updateEditionMedia(slug) {
    if (!slug) return;
    const targetSlug = String(slug).toLowerCase().replace('variant_', '').replace('-edition', '').replace('spot', 'sport').trim();

    const media = document.querySelectorAll('.product__media[data-edition], .product__thumbnail[data-edition]');
    media.forEach(el => {
      const editionAttr = el.dataset.edition || '';
      const editions = editionAttr
        .toLowerCase()
        .split(' ')
        .map(s => s.replace('variant_', '').replace('-edition', '').replace('spot', 'sport').trim())
        .filter(Boolean);
      
      const matches = editions.includes(targetSlug);

      if (matches) {
        el.removeAttribute('hidden');
        el.classList.remove('hidden');
        el.style.display = '';
        const slide = el.closest('.product__media-list > *, .product__thumbnails-list > *');
        if (slide) {
          slide.removeAttribute('hidden');
          slide.classList.remove('hidden');
          slide.style.display = '';
        }
      } else {
        el.setAttribute('hidden', '');
        el.classList.add('hidden');
        el.style.display = 'none';
        const slide = el.closest('.product__media-list > *, .product__thumbnails-list > *');
        if (slide) {
          slide.setAttribute('hidden', '');
          slide.classList.add('hidden');
          slide.style.display = 'none';
        }
      }
    });

    if (!media.length) return;

    //Re-index thumbnails and reset the slider to the first visible item.
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    const dots = gallery.querySelector('media-dots');
    if (dots && typeof dots.resetIndexes === 'function') dots.resetIndexes();
    const slider = gallery.querySelector('slider-element');
    if (slider && typeof slider.select === 'function') slider.select(1);
    if (typeof gallery.countMediaGallery === 'function') gallery.countMediaGallery();
  }

  // ---------------------------------------------------------------------------
  // Availability
  // ---------------------------------------------------------------------------

  async function fetchCover(truckId) {
    if (!truckId) return null;
    return apiGet('availability', {
      truck_id: truckId,
      reporting_category_id: reportingCategoryId
    });
  }

  /**
   * @param {String} truckId Defaults to the bed-length select's value.
   * @param {Object} options track: send "Truck Search" to Klaviyo. Only the shopper's own
   *   Configure Now click sets it -- the saved-cookie restore on every page load is not a search.
   */
  async function submitConfig(truckId, options) {
    if (!truckId) {
      const bedSelect = byId('truck-id');
      truckId = bedSelect ? bedSelect.value : '';
    }
    if (!truckId) {
      console.warn('No Truck ID selected. Cannot submit form.');
      document.documentElement.classList.remove('truck-fitment-prefill');
      return;
    }

    if (options && options.track && window.PeragonFitmentTracking) {
      window.PeragonFitmentTracking.trackSearch(truckId, reportingCategoryId);
    }

    const cover = await fetchCover(truckId);
    //renderCover handles the null/error case (and clears the prefill suppression).
    renderCover(cover, { truckId: truckId });
    announceCover(truckId, cover);

    //Nothing fits: open the waitlist form straight away, as the /shop configurator
    //did. Only for the shopper's own search -- never the saved-cookie restore.
    if (options && options.track && cover && !cover.has_products && cover.has_waitlist) {
      openWaitlistForm();
    }
    fetchFitmentReviews();

    //Only persist when the shopper's own selects reflect this truck -- the cookie
    //prefill path calls submitConfig() with an id before the cascade has caught up,
    //and saving then would write a half-empty cookie over a good one.
    const bedSelect = byId('truck-id');
    if (bedSelect && bedSelect.value === String(truckId)) saveTruckFitmentCookie();
  }

  //"Wishlist" event properties for the truck in the selects -- same keys the /shop
  //configurator sent, so existing Klaviyo flows keep matching.
  function waitlistTruck() {
    const values = stepValues();
    if (!values.bed) return null;
    const bedSelect = byId('truck-id');
    const bedOption = bedSelect && bedSelect.selectedOptions ? bedSelect.selectedOptions[0] : null;
    return {
      'Truck ID': values.bed,
      'Truck Year': values.year,
      'Truck Make': values.make,
      'Truck Model': values.model,
      'Truck Bed Length': bedOption ? bedOption.text : '',
      'Product': reportingCategoryId === CATEGORIES.tectonic.id ? 'Tectonic' : 'Landmark',
      'Source': 'Shopify'
    };
  }

  //Hand the unfitted truck to the waitlist listener (or clear it once a truck fits),
  //so a submit of this block's form is recorded even when Klaviyo opened it itself.
  function rememberWaitlistTruck(unfitted) {
    const formId = waitlistBtn && waitlistBtn.dataset.klaviyoForm;
    if (!formId || !window.PeragonFitmentTracking) return;
    window.PeragonFitmentTracking.setWaitlist(formId, unfitted ? waitlistTruck() : null);
  }

  //Open the block's Klaviyo waitlist form with the selected truck riding along, so
  //the form submit can record it. No-op when the block has no form configured.
  function openWaitlistForm() {
    const formId = waitlistBtn && waitlistBtn.dataset.klaviyoForm;
    if (!formId) return;
    if (window.PeragonFitmentTracking) {
      window.PeragonFitmentTracking.openWaitlist(formId, waitlistTruck());
      return;
    }
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  //Re-check availability after a same-day countdown expires and re-render.
  async function refreshCover(truckId, options) {
    const cover = await fetchCover(truckId);
    if (cover) {
      renderCover(cover, Object.assign({ truckId: truckId }, options));
      announceCover(truckId, cover);
    }
  }

  /**
   * Hourly ship-date refresh. Updates only the shipping slots so it cannot yank the
   * shopper's selected edition out from under them. Now uses the same slug/slot
   * lookup as the renderer, so it works on every line -- it used to build landmark
   * ids only and was a silent no-op on the Tectonic page.
   */
  async function refreshShipDate() {
    const bedSelect = byId('truck-id');
    const truckId = bedSelect ? bedSelect.value : '';
    const cover = await fetchCover(truckId);
    if (!cover || cover.has_error || !cover.has_products) return;

    coverProducts(cover).forEach(product => {
      const edition = resolveEdition(slugify(product.short_name));
      const shipSlot = slot(edition, 'shipping', 'shipping');
      if (!shipSlot) return;
      shipSlot.innerHTML = product['Estimated Ship Date'] || '';
      startShipCountdown(shipSlot, () => refreshCover(truckId, { keepSelection: true }));
    });
  }

  async function fetchFitmentReviews(page) {
    const reviews = await apiGet('reviews', {
      reporting_category_id: reportingCategoryId,
      page: page || 1
    });
    if (reviews) console.log('reviews', reviews);
    //TODO: setFitmentReviews(reviews)
  }

  function changeVehicle() {
    document.documentElement.classList.remove('truck-fitment-prefill');

    //Harmless on single-edition pages -- the selectors just match nothing.
    document.querySelectorAll('#edition-selector .col.edition').forEach(el => el.classList.remove('selected'));
    document.querySelectorAll('#edition-details .details').forEach(el => el.classList.add('hidden'));
    selectedEdition = null;

    //The rendered covers no longer belong to a chosen truck, so neither may their CTAs.
    currentTruck = null;
    document.querySelectorAll('#truck-fitment-root a.add-to-cart-btn').forEach(link => {
      link.href = '#add-to-cart';
      link._cartAdd = null;
    });

    //Clear the saved truck so the configurator starts fresh. The variant param goes
    //with it -- it names an edition of the truck being abandoned.
    document.cookie = 'truck_fitment=; path=/; max-age=0; SameSite=Lax';
    syncVariantParam(null);

    const makeSelect = byId('truck-make');
    if (makeSelect) makeSelect.value = '';
    clearStepsFrom('model');
    announceTruck();

    toggleHidden(byId('configure_fitment'), false);
    toggleHidden(byId('fitment_results'), true);
  }

  // ---------------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------------

  //The ship date can have ticked since render (a same-day countdown), so it is
  //re-read off the DOM at click time rather than reused from render time.
  function clickShipDate(link) {
    return extractCleanShipDate(
      link.querySelector('.shipping') || byId(reportingCategory + '-shipping')
    );
  }

  async function addToCart(link, payload, shipDate) {
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');

    const selection = selectionProperties();

    const body = {
      items: payload.items.map(item => {
        const out = { id: Number(item.id), quantity: item.quantity || 1 };
        const properties = normaliseProperties(
          Object.assign({}, item.properties, selection, truckProperties()),
          shipDate
        );
        if (Object.keys(properties).length) out.properties = properties;
        if (item.selling_plan) out.selling_plan = item.selling_plan;
        return out;
      })
    };

    //Last line of defence: never send a cover to the cart without its truck.
    if (body.items.some(item => !hasTruck(item.properties))) {
      link.removeAttribute('aria-busy');
      blockAddWithoutTruck(link);
      return;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('cart/add.js ' + res.status + ': ' + await res.text());
      window.location.href = '/cart';
    } catch (err) {
      console.error('[truck-fitment] ajax add failed, falling back to permalink', err);
      const href = stampTruckPermalink(normalisePermalink(link.getAttribute('href'), shipDate));
      if (permalinkHasTruck(href)) window.location.href = href;
      else blockAddWithoutTruck(link);
    } finally {
      link.removeAttribute('aria-busy');
    }
  }

  // ---------------------------------------------------------------------------
  // Modals
  // ---------------------------------------------------------------------------

  function bindModals() {
    document.addEventListener('click', function (e) {
      //Open via <a class="popout" href="#modal-id">
      const trigger = e.target.closest('a.popout[href^="#"]');
      if (trigger) {
        e.preventDefault();
        const modal = byId(trigger.getAttribute('href').slice(1));
        if (modal) modal.classList.remove('hidden');
        else console.warn('Modal not found for', trigger.getAttribute('href'));
        return;
      }

      //Close via the close button, or by clicking the overlay outside .content
      const closeBtn = e.target.closest('.fitment-result-modal .close');
      if (closeBtn) {
        e.preventDefault();
        toggleHidden(closeBtn.closest('.fitment-result-modal'), true);
        return;
      }

      const overlay = e.target.closest('.fitment-result-modal');
      if (overlay && !e.target.closest('.fitment-result-modal .content')) {
        toggleHidden(overlay, true);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      document.querySelectorAll('.fitment-result-modal:not(.hidden)')
        .forEach(modal => modal.classList.add('hidden'));
    });

    //Reparent to <body> so page stacking contexts cannot clip the overlay.
    document.querySelectorAll('.fitment-result-modal.modal')
      .forEach(modal => document.body.appendChild(modal));
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  ready(function () {
    root = byId('truck-fitment-root');
    reportingCategory = (root && root.dataset.reportingCategory) || 'landmark';
    const category = CATEGORIES[reportingCategory] || CATEGORIES.landmark;
    reportingCategoryId = category.id;
    priceDecimals = category.priceDecimals;

    //#AchangeR: the client's Shopify-side default (the selected variant's Edition
    //option value, rendered onto the root) leads the preference list, so the edition
    //opened after a truck is chosen matches the one the page painted. The category
    //list stays as the fallback for trucks that edition does not fit.
    const defaultEdition = (root && root.dataset.defaultEdition) || '';
    editionPreference = defaultEdition
      ? [defaultEdition].concat(category.preference.filter(e => e !== defaultEdition))
      : category.preference;

    bindModals();

    //AJAX add to cart, delegated from the fitment results container. The decision to
    //cancel the click has to be made synchronously -- preventDefault() after an await
    //is too late and the browser follows the href anyway.
    if (root) {
      root.addEventListener('click', function (e) {
        const link = e.target.closest('a.add-to-cart-btn');
        if (!link || !root.contains(link)) return;

        const shipDate = clickShipDate(link);
        const payload = link._cartAdd;

        //No AJAX payload yet -- stamp the permalink and let the browser navigate,
        //but only if it carries the truck. "#add-to-cart" (nothing rendered yet) and
        //a bare /cart/add?id= are both stopped here.
        if (!payload || !Array.isArray(payload.items) || !payload.items.length) {
          const href = stampTruckPermalink(normalisePermalink(link.getAttribute('href'), shipDate));
          if (!permalinkHasTruck(href)) {
            e.preventDefault();
            blockAddWithoutTruck(link);
            return;
          }
          link.href = href;
          return;
        }

        e.preventDefault();
        addToCart(link, payload, shipDate);
      });
    }

    //Cascade change handlers, straight off the STEPS table.
    STEPS.forEach((step, index) => {
      const select = stepSelect(step);
      if (!select) return;
      select.addEventListener('change', () => {
        //Only the shopper's own picks fire 'change' -- prefill sets .value directly.
        followTruckToItsPage(step.key);
        const next = STEPS[index + 1];
        if (next) {
          clearStepsFrom(next.key);
          announceTruck();
          populateStep(next.key);
          return;
        }
        //Last step (bed length): restore the submit row so the shopper can
        //re-configure after switching beds, then re-check the button.
        toggleHidden(byId('no-cover-available'), true);
        toggleHidden(byId('truck-submit-div'), false);
        toggleConfigButton();
        announceTruck();
      });
    });

    const configButton = byId('truck-submit');
    if (configButton) configButton.addEventListener('click', () => submitConfig(null, { track: true }));

    //Edition tabs. Bound from the markup that exists rather than a hardcoded list,
    //so a new edition's tab works as soon as the section renders it.
    document.querySelectorAll('#edition-selector .col.edition[id$="-edition"]').forEach(tab => {
      tab.addEventListener('click', () => editionPicked(tab.id.replace(/-edition$/, '')));
    });

    document.querySelectorAll('#change-vehicle, #cover-change-vehicle')
      .forEach(btn => btn.addEventListener('click', () => changeVehicle()));

    //Klaviyo waitlist button (present only when the block setting is configured).
    //The truck being viewed rides along so the form submit can record it as "Wishlist".
    waitlistBtn = document.querySelector('.truck-fitment-waitlist-btn');
    if (waitlistBtn) {
      waitlistBtn.addEventListener('click', e => {
        e.preventDefault();
        openWaitlistForm();
      });
    }

    setInterval(refreshShipDate, ONE_HOUR_MS);

    //URL params win over the cookie so marketing links land on the linked truck even
    //if a stale cookie exists. Exactly one branch runs -- two concurrent cascades
    //wipe each other's selections mid-flight.
    //On a truck cover page the page's own truck sits between the two: a saved truck
    //is only used if it is that truck, so a shopper with a Tundra saved who lands on
    //the F-150 page sees the F-150, not their Tundra.
    const urlTruck = getTruckFitmentFromURL();
    const pageTruck = urlTruck ? null : getTruckFitmentFromPage();
    let savedTruck = getTruckFitmentFromCookie();
    if (pageTruck && !savedTruckMatchesPage(savedTruck, pageTruck)) savedTruck = null;

    if (pageTruck && !savedTruck) {
      //Year page: select through year, leave bed for the shopper. Model page: select
      //make + model and leave the year list open. Make page: select the make only.
      const stopAt = pageTruck.year ? 'bed' : (pageTruck.model ? 'year' : 'model');
      prefillTruck(pageTruck, stopAt)
        .then(() => {
          if (pageTruck.year) saveTruckFitmentCookie();
          toggleConfigButton();
          announceTruck();
        })
        .finally(() => document.documentElement.classList.remove('truck-fitment-prefill'));
    } else if (urlTruck) {
      //Bed options are populated but left unselected; submit stays disabled until the
      //shopper picks one. Persist the partial truck so it carries across navigation.
      prefillTruck(urlTruck, 'bed')
        .then(() => {
          saveTruckFitmentCookie();
          toggleConfigButton();
          announceTruck();
        })
        .finally(() => document.documentElement.classList.remove('truck-fitment-prefill'));
    } else if (savedTruck) {
      //Render the saved truck's covers immediately, in parallel with re-filling the
      //selects, so returning shoppers do not wait on four cascade round-trips. The
      //page content follows the saved truck straight away too, then the selects.
      announceTruck(savedTruck);
      if (savedTruck.id) submitConfig(savedTruck.id);
      prefillTruck(savedTruck)
        .then(() => {
          toggleConfigButton();
          announceTruck();
        })
        .finally(() => document.documentElement.classList.remove('truck-fitment-prefill'));
    } else {
      populateStep('make');
    }
  });

})();